import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "@/App";
import { SUPER_ADMIN_EMAILS } from '@/config/platform';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Pencil,
  X,
  Check,
  Save,
  Undo2,
  Image as ImageIcon,
  Type,
  Eye,
  EyeOff,
  Upload,
  Loader2,
  Settings,
  Send,
  FileText
} from "lucide-react";

// ============================================================================
// ADMIN CONTEXT - Manages edit mode and pending changes with save/publish workflow
// ============================================================================
const AdminContext = createContext(null);

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    return { isAdmin: false, editMode: false };
  }
  return context;
}

export function AdminProvider({ children }) {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [savedContent, setSavedContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPreviewUser, setIsPreviewUser] = useState(false);
  const [editableComponentCount, setEditableComponentCount] = useState(0);

  // Track editable components - call when component mounts/unmounts
  const registerEditableComponent = useCallback(() => {
    setEditableComponentCount(prev => prev + 1);
  }, []);
  
  const unregisterEditableComponent = useCallback(() => {
    setEditableComponentCount(prev => Math.max(0, prev - 1));
  }, []);
  
  // Only show toolbar if there are editable components on the page
  const hasEditableContent = editableComponentCount > 0;

  // Admin email list - super admins who can edit all content
  const ADMIN_EMAILS = SUPER_ADMIN_EMAILS;

  // Check if current user is admin (and not in preview mode)
  useEffect(() => {
    // Check for preview mode first
    try {
      const previewData = JSON.parse(localStorage.getItem('preview_mode') || '{}');
      if (previewData.active) {
        setIsPreviewUser(true);
        setIsAdmin(false); // Preview users are never admins for editing
        setEditMode(false);
        setLoading(false);
        return;
      }
    } catch {
      // Not in preview mode
    }
    
    setIsPreviewUser(false);
    
    if (user?.email) {
      const isUserAdmin = ADMIN_EMAILS.includes(user.email.toLowerCase());
      setIsAdmin(isUserAdmin);
    } else {
      setIsAdmin(false);
    }
    setLoading(false);
  }, [user]);

  // Load saved content from backend (only published content for public, all for admins)
  const loadContent = useCallback(async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/cms/sections`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        const contentMap = {};
        (data.sections || []).forEach(section => {
          // Only load published content for non-admins, or all for admins
          if (section.status === 'published' || isAdmin) {
            contentMap[section.section_id] = section;
          }
        });
        setSavedContent(contentMap);
      }
    } catch (err) {
      console.error("Failed to load CMS content:", err);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // Get content value (pending change or saved)
  const getContent = useCallback((sectionId, field, defaultValue) => {
    // Check pending changes first
    const pendingKey = `${sectionId}.${field}`;
    if (pendingChanges[pendingKey] !== undefined) {
      return pendingChanges[pendingKey];
    }
    // Then check saved content
    const section = savedContent[sectionId];
    if (section) {
      if (section[field] !== undefined) return section[field];
      if (section.custom_fields?.[field] !== undefined) return section.custom_fields[field];
    }
    return defaultValue;
  }, [pendingChanges, savedContent]);

  // Set pending change
  const setContent = useCallback((sectionId, field, value) => {
    const key = `${sectionId}.${field}`;
    setPendingChanges(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Check if there are pending changes
  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  // Save as draft (saves changes but doesn't make them visible to public)
  const saveAsDraft = useCallback(async () => {
    if (!hasPendingChanges) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      
      // Group changes by section
      const sectionUpdates = {};
      Object.entries(pendingChanges).forEach(([key, value]) => {
        const [sectionId, field] = key.split(".");
        if (!sectionUpdates[sectionId]) {
          sectionUpdates[sectionId] = { section_id: sectionId, status: 'draft', custom_fields: {} };
        }
        // Standard fields go at top level, others go in custom_fields
        if (["title", "subtitle", "description", "image"].includes(field)) {
          sectionUpdates[sectionId][field] = value;
        } else {
          sectionUpdates[sectionId].custom_fields[field] = value;
        }
      });

      // Save each section as draft
      for (const [sectionId, data] of Object.entries(sectionUpdates)) {
        const res = await fetch(`${API}/cms/sections`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(data)
        });
        
        if (!res.ok) {
          throw new Error(`Failed to save section ${sectionId}`);
        }
      }

      toast.success("Draft saved! Changes are only visible to admins.");
      setPendingChanges({});
      await loadContent();
    } catch (err) {
      toast.error("Failed to save draft");
      console.error(err);
    } finally {
      setSaving(false);
    }
  }, [pendingChanges, hasPendingChanges, loadContent]);

  // Publish changes (makes them visible to all visitors)
  const publishChanges = useCallback(async () => {
    if (!hasPendingChanges) return;
    
    setPublishing(true);
    try {
      const token = localStorage.getItem("session_token");
      
      // Group changes by section
      const sectionUpdates = {};
      Object.entries(pendingChanges).forEach(([key, value]) => {
        const [sectionId, field] = key.split(".");
        if (!sectionUpdates[sectionId]) {
          sectionUpdates[sectionId] = { section_id: sectionId, status: 'published', custom_fields: {} };
        }
        // Standard fields go at top level, others go in custom_fields
        if (["title", "subtitle", "description", "image"].includes(field)) {
          sectionUpdates[sectionId][field] = value;
        } else {
          sectionUpdates[sectionId].custom_fields[field] = value;
        }
      });

      // Publish each section
      for (const [sectionId, data] of Object.entries(sectionUpdates)) {
        const res = await fetch(`${API}/cms/sections`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(data)
        });
        
        if (!res.ok) {
          throw new Error(`Failed to publish section ${sectionId}`);
        }
      }

      toast.success("Changes published! Now visible to all visitors.");
      setPendingChanges({});
      await loadContent();
    } catch (err) {
      toast.error("Failed to publish changes");
      console.error(err);
    } finally {
      setPublishing(false);
    }
  }, [pendingChanges, hasPendingChanges, loadContent]);

  // Legacy saveChanges - defaults to publish for backwards compatibility
  const saveChanges = useCallback(async () => {
    await publishChanges();
  }, [publishChanges]);

  // Discard all pending changes
  const discardChanges = useCallback(() => {
    setPendingChanges({});
    toast.info("Changes discarded");
  }, []);

  // Toggle edit mode
  const toggleEditMode = useCallback(() => {
    if (editMode && hasPendingChanges) {
      // Warn about unsaved changes
      if (!window.confirm("You have unsaved changes. Discard them?")) {
        return;
      }
      setPendingChanges({});
    }
    setEditMode(prev => !prev);
  }, [editMode, hasPendingChanges]);

  const value = {
    isAdmin,
    editMode: isPreviewUser ? false : editMode, // Never allow edit mode for preview users
    setEditMode: isPreviewUser ? () => {} : setEditMode, // No-op for preview users
    toggleEditMode: isPreviewUser ? () => {} : toggleEditMode, // No-op for preview users
    pendingChanges,
    hasPendingChanges,
    getContent,
    setContent,
    saveChanges,
    saveAsDraft,
    publishChanges,
    discardChanges,
    saving,
    publishing,
    loading,
    isPreviewUser,
    hasEditableContent,
    registerEditableComponent,
    unregisterEditableComponent
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

// ============================================================================
// ADMIN TOOLBAR - Floating toolbar for admins with Save/Publish workflow
// ============================================================================
export function AdminToolbar() {
  const { 
    isAdmin, editMode, toggleEditMode, hasPendingChanges, 
    saveAsDraft, publishChanges, discardChanges, 
    saving, publishing, isPreviewUser, hasEditableContent
  } = useAdmin();

  // Don't show toolbar for non-admins or preview users
  if (!isAdmin || isPreviewUser) return null;
  
  // Don't show toolbar if there are no editable components on this page
  if (!hasEditableContent) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9998] flex flex-col items-end gap-2">
      {/* Pending changes panel with Save Draft / Publish workflow */}
      {editMode && hasPendingChanges && (
        <div className="bg-white rounded-lg shadow-lg border p-4 min-w-[260px]">
          <p className="text-sm font-medium text-amber-600 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Unsaved changes
          </p>
          <div className="flex flex-col gap-2">
            {/* Save as Draft */}
            <Button
              size="sm"
              variant="outline"
              onClick={saveAsDraft}
              disabled={saving || publishing}
              className="w-full justify-start"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Draft
              <span className="ml-auto text-xs text-gray-500">Admin only</span>
            </Button>
            
            {/* Publish */}
            <Button
              size="sm"
              onClick={publishChanges}
              disabled={saving || publishing}
              className="w-full justify-start bg-green-600 hover:bg-green-700"
            >
              {publishing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Publish
              <span className="ml-auto text-xs text-green-200">Live to all</span>
            </Button>
            
            {/* Discard */}
            <Button
              size="sm"
              variant="ghost"
              onClick={discardChanges}
              disabled={saving || publishing}
              className="w-full justify-start text-gray-500 hover:text-red-600"
            >
              <Undo2 className="w-4 h-4 mr-2" />
              Discard changes
            </Button>
          </div>
        </div>
      )}

      {/* Edit mode toggle button */}
      <Button
        onClick={toggleEditMode}
        className={`rounded-full px-4 h-10 shadow-lg ${
          editMode 
            ? "bg-amber-500 hover:bg-amber-600" 
            : "bg-purple-600 hover:bg-purple-700"
        }`}
        data-testid="cms-edit-toggle"
      >
        {editMode ? (
          <>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </>
        ) : (
          <>
            <Pencil className="w-4 h-4 mr-2" />
            Edit Page
          </>
        )}
      </Button>
    </div>
  );
}

// ============================================================================
// EDITABLE TEXT - Inline text editing component
// ============================================================================
export function EditableText({
  sectionId,
  field,
  defaultValue = "",
  className = "",
  as = "span",
  multiline = false,
  placeholder = "Click to edit..."
}) {
  const { editMode, getContent, setContent, registerEditableComponent, unregisterEditableComponent } = useAdmin();
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");
  const inputRef = useRef(null);

  const currentValue = getContent(sectionId, field, defaultValue);

  // Register this component when mounted, unregister when unmounted
  useEffect(() => {
    registerEditableComponent();
    return () => unregisterEditableComponent();
  }, [registerEditableComponent, unregisterEditableComponent]);

  useEffect(() => {
    setLocalValue(currentValue);
  }, [currentValue]);

  const handleClick = () => {
    if (editMode && !isEditing) {
      setIsEditing(true);
      setLocalValue(currentValue);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (localValue !== currentValue) {
      setContent(sectionId, field, localValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === "Escape") {
      setLocalValue(currentValue);
      setIsEditing(false);
    }
  };

  const Tag = as;

  // Not in edit mode - just render the text
  if (!editMode) {
    return <Tag className={className}>{currentValue || defaultValue}</Tag>;
  }

  // In edit mode but not actively editing - show clickable element
  if (!isEditing) {
    return (
      <Tag
        className={`${className} cursor-pointer hover:outline hover:outline-2 hover:outline-dashed hover:outline-purple-400 hover:outline-offset-2 transition-all`}
        onClick={handleClick}
        data-testid={`editable-${sectionId}-${field}`}
      >
        {currentValue || <span className="text-gray-400 italic">{placeholder}</span>}
      </Tag>
    );
  }

  // Actively editing
  if (multiline) {
    return (
      <Textarea
        ref={inputRef}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={`${className} min-h-[100px] border-purple-400 focus:ring-purple-400`}
        placeholder={placeholder}
      />
    );
  }

  return (
    <Input
      ref={inputRef}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`${className} border-purple-400 focus:ring-purple-400`}
      placeholder={placeholder}
    />
  );
}

// ============================================================================
// EDITABLE IMAGE - Image upload/replace component
// ============================================================================
export function EditableImage({
  sectionId,
  field,
  defaultSrc = "",
  defaultImage = null, // Alias for defaultSrc
  className = "",
  alt = "Image",
  objectFit = "cover",
  placeholderClassName = "bg-gray-100",
  placeholderIcon = null
}) {
  const { editMode, getContent, setContent, registerEditableComponent, unregisterEditableComponent } = useAdmin();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const currentSrc = getContent(sectionId, field, defaultSrc || defaultImage || "");

  // Register this component when mounted, unregister when unmounted
  useEffect(() => {
    registerEditableComponent();
    return () => unregisterEditableComponent();
  }, [registerEditableComponent, unregisterEditableComponent]);

  const handleClick = () => {
    if (editMode) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem("session_token");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("section_id", sectionId);
      formData.append("field", field);

      const res = await fetch(`${API}/cms/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setContent(sectionId, field, data.url);
        toast.success("Image uploaded!");
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      toast.error("Failed to upload image");
      console.error(err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const imageStyle = {
    objectFit: objectFit
  };

  // Default placeholder icon
  const PlaceholderContent = placeholderIcon || <ImageIcon className="w-8 h-8 text-gray-300" />;

  if (!editMode) {
    return currentSrc ? (
      <img src={currentSrc} alt={alt} className={className} style={imageStyle} />
    ) : (
      <div className={`${className} ${placeholderClassName} flex items-center justify-center`}>
        {PlaceholderContent}
      </div>
    );
  }

  return (
    <div
      className={`${className} relative cursor-pointer group`}
      onClick={handleClick}
      data-testid={`editable-image-${sectionId}-${field}`}
    >
      {currentSrc ? (
        <img src={currentSrc} alt={alt} className="w-full h-full" style={imageStyle} />
      ) : (
        <div className={`w-full h-full ${placeholderClassName} flex items-center justify-center`}>
          {PlaceholderContent}
        </div>
      )}
      
      {/* Overlay on hover */}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        {uploading ? (
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        ) : (
          <div className="text-white text-center">
            <Upload className="w-8 h-8 mx-auto mb-1" />
            <span className="text-sm">Click to upload</span>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}

// ============================================================================
// EDITABLE SECTION - Visual wrapper showing section boundaries
// ============================================================================
export function EditableSection({
  sectionId,
  children,
  className = "",
  label = null
}) {
  const { editMode } = useAdmin();

  if (!editMode) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      className={`${className} relative outline outline-2 outline-dashed outline-purple-300 outline-offset-4`}
      data-testid={`section-${sectionId}`}
    >
      {/* Section label */}
      <div className="absolute -top-3 left-2 bg-purple-600 text-white text-xs px-2 py-0.5 rounded z-10">
        {label || sectionId}
      </div>
      {children}
    </div>
  );
}

// ============================================================================
// EDITABLE RICH CONTENT - For larger content blocks
// ============================================================================
export function EditableRichContent({
  sectionId,
  field,
  defaultValue = "",
  className = "",
  placeholder = "Click to edit content..."
}) {
  const { editMode, getContent, setContent } = useAdmin();
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");

  const currentValue = getContent(sectionId, field, defaultValue);

  useEffect(() => {
    setLocalValue(currentValue);
  }, [currentValue]);

  const handleSave = () => {
    if (localValue !== currentValue) {
      setContent(sectionId, field, localValue);
    }
    setIsEditing(false);
  };

  if (!editMode) {
    return (
      <div className={className} dangerouslySetInnerHTML={{ __html: currentValue || defaultValue }} />
    );
  }

  if (!isEditing) {
    return (
      <div
        className={`${className} cursor-pointer hover:outline hover:outline-2 hover:outline-dashed hover:outline-purple-400 hover:outline-offset-2`}
        onClick={() => setIsEditing(true)}
        data-testid={`editable-rich-${sectionId}-${field}`}
      >
        {currentValue ? (
          <div dangerouslySetInnerHTML={{ __html: currentValue }} />
        ) : (
          <span className="text-gray-400 italic">{placeholder}</span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Textarea
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        className="min-h-[200px] font-mono text-sm"
        placeholder={placeholder}
      />
      <div className="flex gap-2">
        <Button size="sm" onClick={handleSave}>
          <Check className="w-4 h-4 mr-1" />
          Done
        </Button>
        <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
          <X className="w-4 h-4 mr-1" />
          Cancel
        </Button>
      </div>
    </div>
  );
}
